import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import type { IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { MorphemeInfoPanelAccordionStickyHeader } from "#morpheme-info-panel-accordion-sticky-header";
import { MorphemeInfoPanelAccordionScrollControlProvider } from "#morpheme-info-panel-accordion-scroll-control-provider";
import type { MorphemeInfoPanelAccordionScrollView } from "#morpheme-info-panel-accordion-scroll-control";
import { useRef } from "react";

/**
 * Loading state for a info tab whose slice hasn't resolved yet. Rendered as the
 * `SuspensePageBoundary` fallback for the info view — the Learn card's info scene and the info
 * dictionary screen's inner boundary — so a defensive cold-query path reads as a loading panel
 * instead of a blank/generic-spinner one.
 * It renders the section header (e.g. "Overview") with a spinner in place of the chevron, and a
 * second centered spinner filling the body so the panel unmistakably reads as "loading" (a
 * header-only spinner over a light-mode `bg-muted` body looked blank). The header is inert (no body,
 * no toggle). Depends on NO panel context — it renders before the info slice resolves, so
 * `MorphemeInfoPanelContext` isn't available yet.
 */
export function MorphemeInfoPanelLoadingView({
  iconName = "LuBookOpen",
  title = "Overview",
}: {
  iconName?: IconName;
  title?: string;
}) {
  // The sticky header reads the scroll-control context (a default-error proxy otherwise); supply
  // inert refs so the provider exists. The header is disabled via `isLoading`, so its press
  // handlers never read these.
  const scrollRef = useRef<MorphemeInfoPanelAccordionScrollView | null>(null);
  const scrollYRef = useRef(0);

  return (
    <View className="flex-1 bg-muted">
      <MorphemeInfoPanelAccordionScrollControlProvider
        scrollRef={scrollRef}
        scrollYRef={scrollYRef}
      >
        <MorphemeInfoPanelAccordionStickyHeader
          iconName={iconName}
          isLoading
          isOpen={false}
          onToggle={() => {}}
          title={title}
        />
      </MorphemeInfoPanelAccordionScrollControlProvider>
      <View className="flex-1 items-center justify-center bg-surface">
        <ActivityIndicator />
      </View>
    </View>
  );
}
